import React, { useState, useEffect } from 'react';
import { auth } from '../config';
import { onAuthStateChanged } from 'firebase/auth';
import getUser from '../api/get/getUser';

const ServiceInfo = () => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setUser(user);
                try {
                    const data = await getUser({ user_id: user.uid });
                    setUserData(data);
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
            } else {
                setUser(null);
                setUserData(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, var(--app-color), #8B5CF6)'}}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
                    <p className="mt-4 text-white">Жүктелуде...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{background: 'linear-gradient(135deg, var(--app-color), #8B5CF6)'}}>
            {/* Header */}
            <header className="bg-white/90 backdrop-blur-sm border-b border-white/20">
                <div className="max-w-6xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background: 'var(--app-color)'}}>
                                <span className="text-white font-bold text-lg">E</span>
                            </div>
                            <h1 className="text-xl font-bold text-gray-900">Esti.kz</h1>
                        </div>
                        {user && (
                            <div className="flex items-center gap-3">
                                <div className="text-right">
                                    <p className="text-sm font-medium text-gray-900">
                                        {userData?.name || user.displayName || 'Пайдаланушы'}
                                    </p>
                                    <p className="text-xs text-gray-500">{user.email}</p>
                                </div>
                                <div className="w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center">
                                    {userData?.photoURL ? (
                                        <img 
                                            src={userData.photoURL} 
                                            alt="Avatar" 
                                            className="w-7 h-7 rounded-full"
                                        />
                                    ) : (
                                        <span className="text-xs font-medium text-gray-600">
                                            {user.email?.charAt(0).toUpperCase()}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-4 py-8">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-white mb-3">
                        Esti.kz-ке қош келдіңіз
                    </h2>
                    <p className="text-lg text-white/90 max-w-2xl mx-auto">
                        Естір құлаққа есті сөз
                    </p>
                </div>

                {/* Service Features */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-5 border border-white/20">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{background: 'var(--app-color)'}}>
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                            </svg>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-2">Аудиокітаптар</h3>
                        <p className="text-sm text-gray-600">
                            Барлық жастағы адамдарға арналған қазақ тіліндегі аудиокітаптардың үлкен жинағы.
                        </p>
                    </div>

                    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-5 border border-white/20">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{background: 'var(--app-color)'}}>
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-2">Қауіпсіздік</h3>
                        <p className="text-sm text-gray-600">
                            Сіздің деректеріңіз заманауи шифрлау әдістерімен қорғалған.
                        </p>
                    </div>

                    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-5 border border-white/20">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{background: 'var(--app-color)'}}>
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-2">Сапа</h3>
                        <p className="text-sm text-gray-600">
                            Кәсіби дикторлардың жоғары сапалы оқуы.
                        </p>
                    </div>
                </div>

                {/* User Account Info */}
                {user && userData && (
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6 mb-6 border border-white/20">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Тіркелгі туралы ақпарат</h3>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="text-lg font-semibold text-gray-700 mb-3">Жеке ақпарат</h4>
                                <div className="space-y-2">
                                    <p><span className="font-medium">Аты:</span> {userData.name || 'Көрсетілмеген'}</p>
                                    <p><span className="font-medium">Email:</span> {user.email}</p>
                                    <p><span className="font-medium">Тіркеу күні:</span> {new Date(user.metadata.creationTime).toLocaleDateString('kk-KZ')}</p>
                                </div>
                            </div>
                            <div>
                                <h4 className="text-lg font-semibold text-gray-700 mb-3">Статистика</h4>
                                <div className="space-y-2">
                                    <p><span className="font-medium">Күйі:</span> <span className="text-green-600">Белсенді</span></p>
                                    <p><span className="font-medium">Соңғы кіру:</span> {new Date(user.metadata.lastSignInTime).toLocaleDateString('kk-KZ')}</p>
                                    <p><span className="font-medium">Қол жетімділік деңгейі:</span> Пайдаланушы</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Fixed Download Banner */}
                <div className="fixed bottom-4 left-4 right-4 z-50">
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-4 border border-white/20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: 'var(--app-color)'}}>
                                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="text-base font-semibold text-gray-900">
                                        {(() => {
                                            const isAndroid = /Android/i.test(navigator.userAgent);
                                            const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                                            if (isAndroid) return "Android қосымшасы";
                                            if (isIOS) return "iOS қосымшасы";
                                            return "Қосымша";
                                        })()}
                                    </h4>
                                    <p className="text-xs text-gray-500">
                                        Толық тәжірибе
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <button 
                                    onClick={() => {
                                        const isAndroid = /Android/i.test(navigator.userAgent);
                                        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                                        const googlePlayLink = "https://play.google.com/store/apps/details?id=com.qteam.esti_kz&pcampaignid=web_share";
                                        const appleStoreLink = "https://apps.apple.com/kz/app/esti-kz/id6747961818";
                                        const link = isAndroid ? googlePlayLink : isIOS ? appleStoreLink : googlePlayLink;
                                        window.open(link, '_blank');
                                    }}
                                    className="text-white px-4 py-2 rounded-xl font-medium flex items-center gap-1.5 text-sm transition-all duration-200 hover:scale-105 active:scale-95"
                                    style={{background: 'var(--app-color)'}}
                                >
                                    Жүктеу
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                                    </svg>
                                </button>
                                <button 
                                    onClick={() => {
                                        const banner = document.querySelector('.fixed.bottom-4');
                                        if (banner) banner.style.display = 'none';
                                    }}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Links */}
                <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6 mb-20 border border-white/20">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Пайдалы сілтемелер</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <a href="/privacy-policy" className="block p-3 bg-white/50 backdrop-blur-sm border border-white/30 rounded-lg hover:bg-white/70 hover:border-opacity-50 transition-all duration-200 hover:scale-105 hover:shadow-md">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{background: 'var(--app-color)'}}>
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Құпиялылық саясаты</h4>
                                    <p className="text-xs text-gray-600">Деректеріңізді қалай қорғаймыз</p>
                                </div>
                            </div>
                        </a>
                        <a href="/terms-of-use" className="block p-3 bg-white/50 backdrop-blur-sm border border-white/30 rounded-lg hover:bg-white/70 hover:border-opacity-50 transition-all duration-200 hover:scale-105 hover:shadow-md">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{background: 'var(--app-color)'}}>
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Пайдалану шарттары</h4>
                                    <p className="text-xs text-gray-600">Сервисті пайдалану ережелері</p>
                                </div>
                            </div>
                        </a>
                        <a href="/cookie-policy" className="block p-3 bg-white/50 backdrop-blur-sm border border-white/30 rounded-lg hover:bg-white/70 hover:border-opacity-50 transition-all duration-200 hover:scale-105 hover:shadow-md">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{background: 'var(--app-color)'}}>
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Cookie саясаты</h4>
                                    <p className="text-xs text-gray-600">Cookie файлдарын пайдалану</p>
                                </div>
                            </div>
                        </a>
                        <a href="/payment-security" className="block p-3 bg-white/50 backdrop-blur-sm border border-white/30 rounded-lg hover:bg-white/70 hover:border-opacity-50 transition-all duration-200 hover:scale-105 hover:shadow-md">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{background: 'var(--app-color)'}}>
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Төлем қауіпсіздігі</h4>
                                    <p className="text-xs text-gray-600">Қауіпсіздік туралы ақпарат</p>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-white/10 backdrop-blur-sm border-t border-white/20 py-6 mt-8">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="text-center">
                        <p className="text-white/80 text-sm">
                            © 2024 Esti.kz. Барлық құқықтар қорғалған.
                        </p>
                        <p className="text-xs text-white/60 mt-1">
                            Отбасы хрестоматиясы
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ServiceInfo;
